if (!customElements.get('st-peek-slideshow')) {
  class StPeekSlideshow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-peek-track]');
      this.slides = [...this.querySelectorAll('[data-peek-slide]')];
      if (!this.track || this.slides.length === 0) return;

      this.total = this.slides.length;
      if (this.total < 2) {
        this.setAttribute('data-single', '');
        this.centerSingle();
        return;
      }

      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.autoplayTimer = null;
      this.isTransitioning = false;
      this.internalIndex = 1; // Start at real first slide (index 0 is clone)

      this.allSlides = null; // cached after clones
      this.setupClones();
      this.allSlides = [...this.track.children];
      this.updatePosition(false);
      this.updateActiveSlide();

      // Events
      var self = this;
      var prevBtn = this.querySelector('[data-peek-prev]');
      var nextBtn = this.querySelector('[data-peek-next]');
      if (prevBtn) prevBtn.addEventListener('click', function () { self.prev(); });
      if (nextBtn) nextBtn.addEventListener('click', function () { self.next(); });
      // transitionend is not guaranteed (a cancelled or interrupted transition
      // never fires it), so every animated move also arms a fallback timer.
      this.track.addEventListener('transitionend', function (e) {
        if (e.target === self.track && e.propertyName === 'transform') self.settle();
      });
      this.track.addEventListener('transitioncancel', function (e) {
        if (e.target === self.track && e.propertyName === 'transform') self.settle();
      });

      this.setupTouch();

      // Resize
      // Phones fire resize whenever the address bar shows or hides while
      // scrolling; only a width change needs the track re-measured.
      var resizeTimeout;
      var lastWidth = this.offsetWidth;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(function () {
          if (self.offsetWidth === lastWidth) return;
          lastWidth = self.offsetWidth;
          self.settle();
        }, 100);
      });

      // Autoplay
      if (this.autoplayEnabled) this.startAutoplay();

      // Pause on hover
      this.addEventListener('mouseenter', function () { self.pauseAutoplay(); });
      this.addEventListener('mouseleave', function () {
        if (self.autoplayEnabled) self.startAutoplay();
      });
    }

    centerSingle() {
      // Single-slide banner: CSS handles centering via justify-content + margin.
      // Clear any inline transform so we don't fight the flex layout.
      this.track.style.transform = '';
      this.track.style.transition = 'none';
    }

    setupClones() {
      var first = this.slides[0].cloneNode(true);
      var last = this.slides[this.total - 1].cloneNode(true);
      first.setAttribute('aria-hidden', 'true');
      last.setAttribute('aria-hidden', 'true');
      first.removeAttribute('id');
      last.removeAttribute('id');
      this.track.appendChild(first);
      this.track.insertBefore(last, this.track.firstChild);
    }

    getSlideWidth() {
      var allSlides = this.track.children;
      if (allSlides.length === 0) return 0;
      return allSlides[0].offsetWidth;
    }

    getGap() {
      return parseFloat(getComputedStyle(this.track).gap) || 0;
    }

    updatePosition(animate) {
      var slideWidth = this.getSlideWidth();
      var gap = this.getGap();
      var containerWidth = this.offsetWidth;
      var offset = (containerWidth - slideWidth) / 2;
      var translateX = -(this.internalIndex * (slideWidth + gap)) + offset;

      if (animate) {
        this.track.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)';
      } else {
        this.track.style.transition = 'none';
      }
      this.track.style.transform = 'translateX(' + translateX + 'px)';

      clearTimeout(this.settleTimer);
      if (animate) {
        this.isTransitioning = true;
        var self = this;
        this.settleTimer = setTimeout(function () { self.settle(); }, 650);
      }
      this.updateActiveSlide();
    }

    updateActiveSlide() {
      if (!this.allSlides) return;
      for (var i = 0; i < this.allSlides.length; i++) {
        if (i === this.internalIndex) {
          this.allSlides[i].setAttribute('data-active', '');
        } else {
          this.allSlides[i].removeAttribute('data-active');
        }
      }
    }

    // Finish the current move: unlock, and if we are sitting on a clone jump
    // to the matching real slide without animation so the loop continues.
    settle() {
      clearTimeout(this.settleTimer);
      this.isTransitioning = false;
      var onClone = this.internalIndex <= 0 || this.internalIndex >= this.total + 1;
      if (this.internalIndex <= 0) this.internalIndex = this.total;
      else if (this.internalIndex >= this.total + 1) this.internalIndex = 1;
      if (onClone) this.setAttribute('data-jumping', '');
      this.updatePosition(false);
      void this.track.offsetWidth; // commit the un-animated position before any new move
      if (onClone) {
        var self = this;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { self.removeAttribute('data-jumping'); });
        });
      }
    }

    // A new move during an animation finishes that animation first, so a
    // quick second swipe is never dropped.
    next() {
      if (this.isTransitioning) this.settle();
      this.internalIndex++;
      this.updatePosition(true);
      this.restartAutoplay();
    }

    prev() {
      if (this.isTransitioning) this.settle();
      this.internalIndex--;
      this.updatePosition(true);
      this.restartAutoplay();
    }

    startAutoplay() {
      this.pauseAutoplay();
      var self = this;
      this.autoplayTimer = setInterval(function () { self.next(); }, this.speed);
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      if (this.autoplayEnabled) this.startAutoplay();
    }

    setupTouch() {
      var self = this;
      var startX = 0;
      var startY = 0;
      var dragging = false;

      this.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        dragging = true;
      }, { passive: true });

      this.addEventListener('touchend', function (e) {
        if (!dragging) return;
        dragging = false;
        var dx = e.changedTouches[0].clientX - startX;
        var dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
          if (dx < 0) self.next();
          else self.prev();
        }
      }, { passive: true });
    }

    disconnectedCallback() {
      this.pauseAutoplay();
    }
  }

  customElements.define('st-peek-slideshow', StPeekSlideshow);
}
